import React from 'react';
import { jss } from '~/tools/jss';
import { Avatar } from '~/components';
import Button from '~/components/Button';
import { Link } from 'react-router-dom';
import { WorkTangoCoach } from '~/views/UI.shared/WorkTangoCoach/WorkTangoCoach';
import { useAppDispatch } from '~/behaviors/redux';
import { userActions } from '~/behaviors/actions/user.actions';

const useStyles = jss.makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    height: theme.layout.headerHeight,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    alignItems: 'center',
    alignContent: 'stretch',
    justifyContent: 'flex-end',
    paddingLeft: '24px',
    paddingRight: '40px',
  },
  companyLink: {
    display: 'block',
    height: 40,
  },
  logo: {
    height: '100%',
  },
  topRightToolsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
  logoContainer: {
    flexGrow: 1,
  },
  impersonationBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 12px',
    borderRadius: 4,
    backgroundColor: '#FFF3CD',
    color: '#664D03',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
}));

type ContainerProps = {
  className: string;
  canSeeSettings: boolean;
  children: React.ReactNode;
};

// A container for the company logo which either wraps it in a link to the
// company-settings page, or, if user does not have permissions, a <div>.
/* istanbul ignore start */
const CompanyLogoContainer = ({ className = '', canSeeSettings = false, children }: ContainerProps) => {
  /* istanbul ignore stop */
  if (!canSeeSettings) {
    return <div className={className}>{children}</div>;
  }

  return (
    <Link to="/settings" className={className} data-testid="header-company-logo">
      {children}
    </Link>
  );
};

type HeaderProps = {
  name?: string;
  color?: string;
  avatarUrl?: string;
  companyName?: string;
  companyLogoUrl?: string;
  canSeeSettings?: boolean;
  isImpersonating?: boolean;
};

const Header = ({
  name = '',
  color = '#ffffff',
  avatarUrl = '',
  companyName = '',
  companyLogoUrl = '',
  canSeeSettings = false,
  isImpersonating = false,
}: HeaderProps) => {
  const styles = useStyles();
  const dispatch = useAppDispatch();

  const handleExitImpersonation = () => {
    // @ts-ignore @TODO Epic WT-21600
    dispatch(userActions.exitImpersonation());
  };

  return (
    <div className={styles.root} style={{ backgroundColor: color }}>
      {companyLogoUrl && (
        <div className={styles.logoContainer}>
          <CompanyLogoContainer className={styles.companyLink} canSeeSettings={canSeeSettings}>
            <img src={companyLogoUrl} alt={companyName} className={styles.logo} />
          </CompanyLogoContainer>
        </div>
      )}

      <div className={styles.topRightToolsContainer}>
        {isImpersonating && (
          <div className={styles.impersonationBanner} data-testid="impersonation-banner">
            <span>Currently impersonating {name}</span>
            <Button
              color="primary"
              variant="outlined"
              onClick={handleExitImpersonation}
              data-testid="exit-impersonation-button"
            >
              Exit
            </Button>
          </div>
        )}
        <div>
          <WorkTangoCoach />
        </div>
        <div>
          <Avatar avatarUrl={avatarUrl} alt={name} to="/profile" />
        </div>
      </div>
    </div>
  );
};

export default Header;
